import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';

import { titleEditorStoreSelector, useTitleEditorStore } from '@warehouse/title/domain';
import { useExportTitle } from '../../../../src/utils/hooks/titles/useExportMEC';
import { useImportMEC } from '../../../../src/utils/hooks/titles/useImportMEC';
import { AutoButtonGroup } from '../../../../src/components/library/AutoButtonGroup';
import { getExportedTitleFileName } from '../get-exported-title-file-name';
import titleSearchDisplayNameBuilder from '../../../../src/utils/titles/titleSearchDisplayNameBuilder';
import { MonacoEditorDialog } from './XMLEditor/MonacoEditorDialog';

export function ExportMECButton() {
	const title = useTitleEditorStore(titleEditorStoreSelector.title);
	const { loadExport, data, loading, error, clear } = useExportTitle();
	const { importMEC, loading: importLoading, errors: importErrors, clearErrors } = useImportMEC();
	const [editorOpen, setEditorOpen] = useState<boolean>(false);
	const [isSaveButtonEnabled, setIsSaveButtonEnabled] = useState<boolean>(false);
	const [errors, setErrors] = useState<string[]>([]);
	const { enqueueSnackbar } = useSnackbar();
	const [exportType, setExportType] = useState<string | undefined>(undefined);

	const createDownloadableFile = useCallback(
		(content: string, fileExtension: 'xml' | 'json') => {
			try {
				const blob = new Blob([content], { type: 'text/plain' });

				const url = URL.createObjectURL(blob);
				const link = document.createElement('a');
				link.href = url;
				link.download = getExportedTitleFileName({ title, fileExtension });
				document.body.appendChild(link);
				link.click();

				URL.revokeObjectURL(url);
				document.body.removeChild(link);
			} catch (err: any) {
				enqueueSnackbar(err.message || 'Failed to create downloadable file', {
					variant: 'error',
					autoHideDuration: 2000,
				});
			}
		},
		[title, enqueueSnackbar],
	);

	// Handle data when it's ready after export
	useEffect(() => {
		if (!loading && data) {
			const mecContent = data;

			switch (exportType) {
				case 'xml':
					createDownloadableFile(mecContent, 'xml');
					clear();
					break;
				case 'open-xml':
					setEditorOpen(true);
					break;
				default:
					break;
			}
		}
	}, [loading, data, createDownloadableFile, exportType, clear]);

	useEffect(() => {
		if (importErrors.length > 0) {
			setErrors(importErrors);
		}
	}, [importErrors]);

	const handleCloseEditor = useCallback(() => {
		setEditorOpen(false);
		clear();
		clearErrors();
		setErrors([]);
	}, [clear, clearErrors]);

	const originalContentID = useMemo(() => data?.match(/<mdmec:Basic[^>]*ContentID="([^"]+)"/)?.[1], [data]);

	const validateContentID = useCallback(
		(mec: string) => {
			const mecContentID = mec.match(/<mdmec:Basic[^>]*ContentID="([^"]+)"/)?.[1];

			const contentIDMatches = originalContentID && mecContentID && mecContentID === originalContentID;

			if (!contentIDMatches) {
				setIsSaveButtonEnabled(false);
				setErrors(['ContentID was modified']);
			} else {
				setIsSaveButtonEnabled(true);
				setErrors((currentErrors) => currentErrors.filter((err) => err !== 'ContentID was modified'));
			}
		},
		[originalContentID],
	);

	const onSaveButton = useCallback(
		(mec: string) => {
			importMEC(mec);
		},
		[importMEC],
	);

	const handleContentChange = useCallback(
		(mec: string) => {
			validateContentID(mec);
		},
		[validateContentID],
	);

	const handleClick = useCallback(
		(type: string) => {
			clear();
			setExportType(type);
			loadExport(title.uuid, 'xml');
		},
		[loadExport, clear, title.uuid],
	);

	const exportMecOptions = useMemo(
		() => [
			{ id: 'xml', label: 'Export MEC', default: true },
			{ id: 'open-xml', label: 'Open MEC' },
		],
		[],
	);

	const headerTitle = useMemo(() => {
		const tokens = title?.readonly?.titleSearchDisplayNameFullHTML;
		return titleSearchDisplayNameBuilder({ tokens }).title;
	}, [title?.readonly?.titleSearchDisplayNameFullHTML]);

	return (
		<>
			<AutoButtonGroup
				nxstyle="secondary-black"
				loading={loading}
				storeKey="export-format"
				height={32}
				options={exportMecOptions}
				onOptionClick={handleClick}
				expandButtonAriaLabel="Export options menu"
			/>
			{!error && (
				<MonacoEditorDialog
					open={editorOpen}
					onClose={handleCloseEditor}
					content={data}
					title={headerTitle}
					subtitle={title.uuid}
					language="xml"
					errors={errors}
					onSaveHandler={onSaveButton}
					onChange={handleContentChange}
					loading={importLoading}
					isSaveButtonEnabled={isSaveButtonEnabled}
				/>
			)}
		</>
	);
}
